import { Button, CircularProgress, Stack, Theme, Typography } from '@mui/material';
import { FC } from 'react';

import style from './style';
import { WEBSITE_STIL } from '@/constants/websiteStil';
import { IconType } from '@/assets/icons';
import Icon from '@/components/ui/Icon';
import { SXStyleSheet } from '@/utils';
import { bm } from '@/theme/bausteinMasse';

export type CTAButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'outlined'
  | 'danger'
  | 'dark'
  | 'outlinedOnDark'
  | 'website'
  | 'link';

export interface CTAButtonProps {
  label?: string;
  onClick?: () => void;
  variant?: CTAButtonVariant;
  disabled?: boolean;
  loading?: boolean;
  icon?: IconType;
  /**
   * Seite, auf der `icon` steht. Default `start` = Bestandsverhalten
   * (Icon vor dem Label); `end` setzt es hinter das Label.
   * Additiv eingefuehrt 21.08.2026 fuer den Foto-Hinweis am
   * Upload-CTA der Kunden-Landingpage (Entscheid Alex).
   */
  iconPosition?: 'start' | 'end';
  size?: 'small' | 'normal' | 'large';
  fixedWidth?: number;
  autoWidth?: boolean;
  fontSize?: number;
  type?: 'button' | 'submit' | 'reset';
  'data-testid'?: string;
}

const getTextColor = (variant: CTAButtonVariant): string => {
  if (variant === 'tertiary') return 'text.secondary';
  if (variant === 'outlined' || variant === 'link') return 'primary.main';
  if (variant === 'danger') return 'error.main';
  if (variant === 'website') return WEBSITE_STIL.navyTief;
  if (variant === 'outlinedOnDark') return 'common.white';
  // Dark Mode (Decision #248): Navy-Schrift auf Gold nur im Dunkeln — im
  // Hellen ist `secondary.contrastText` dasselbe Weiss wie bisher.
  if (variant === 'secondary') return 'secondary.contrastText';
  return 'primary.contrastText';
};

/**
 * Varianten mit EIGENER Deaktiviert-Optik — die helle `disabledText`
 * (gedämpfte dunkle Schrift auf `grey.50`) gilt dort nicht: `website` bleibt
 * Gold (Alex 31.07.2026), `outlinedOnDark` steht auf dunklem Grund und bleibt
 * weiß durchscheinend (Design-Fix 19.10.2026, design-system.md §2).
 */
const OHNE_HELLE_DEAKTIVIERT_OPTIK: CTAButtonVariant[] = ['website', 'outlinedOnDark'];

const getDefaultFontSize = (variant: CTAButtonVariant): number | ((theme: Theme) => number) => {
  // Design-Rollout 25.10.2026: alle Hausvarianten 14 (Theme-Rolle Knopf
  // 14/500); nur die Website-Variante der Kundenseiten bleibt bei 16.
  if (variant === 'website') return 16;
  if (variant === 'tertiary') return 14;
  // Aus dem Theme: CRM 14, Alt-Theme der Beratungsstrecke 13 (Original).
  return (theme: Theme) => bm(theme).knopf.schrift;
};

const CTAButton: FC<CTAButtonProps> = ({
  label,
  onClick,
  variant = 'primary',
  loading,
  icon,
  iconPosition = 'start',
  size = 'normal',
  fixedWidth,
  autoWidth,
  disabled,
  fontSize,
  type = 'button',
  'data-testid': dataTestId,
}) => {
  return (
    <Button
      type={type}
      data-testid={dataTestId}
      sx={SXStyleSheet.merge(
        style.container,
        // Die Textfarbe der Variante auch an der WURZEL (Design-Paket 3,
        // 11.09.2026): das Label traegt sie ohnehin, aber ein `icon` erbt
        // `currentColor` von hier — und `color="inherit"` unten liesse es
        // sonst die Seitenfarbe erben (grauer Pfeil auf goldenem Knopf).
        { color: getTextColor(variant) },
        style[variant],
        style[size],
        !!fixedWidth && { width: fixedWidth },
        autoWidth && style.autoWidthContainer,
        disabled && style.disabled,
        disabled && !OHNE_HELLE_DEAKTIVIERT_OPTIK.includes(variant) && style.disabledText,
      )}
      disableElevation
      disableRipple
      disableFocusRipple
      disableTouchRipple
      onClick={onClick}
      variant="contained"
      disabled={disabled}
      color="inherit"
    >
      {!!icon && iconPosition === 'start' && (
        <Stack sx={SXStyleSheet.merge(!!label && style.iconContainer)}>
          <Icon icon={icon} />
        </Stack>
      )}
      {loading ? (
        <CircularProgress size={16} sx={style.progress} />
      ) : label ? (
        <Typography
          variant="button"
          color={getTextColor(variant)}
          sx={{ fontSize: fontSize ?? getDefaultFontSize(variant) }}
        >
          {label}
        </Typography>
      ) : null}
      {!!icon && iconPosition === 'end' && !loading && (
        <Stack sx={SXStyleSheet.merge(!!label && style.iconContainerEnd)}>
          <Icon icon={icon} />
        </Stack>
      )}
    </Button>
  );
};

export default CTAButton;
